// ROTINA, EM DUAS FASES — 12-prompt-objetivo-vita.md § 3 (linha StepSchedule).
//
// O step continua sendo UM item de `baseSteps` e UM único ponto de escrita no
// `SCHEDULE`. O que ele tem é uma fase interna:
//
//   fase 'sono'   → acorda + dorme, com a duração como legenda (não input)
//   fase 'treino' → período (Manhã/Tarde/Noite) + a hora
//
// "Continuar" na fase de sono NÃO chama o `next()` do orquestrador: só troca a
// fase. Quem chama o `next()` é o botão da fase de treino. Assim o
// `FormsObjetivo`, o índice de progresso e o payload não mudam — era a
// alternativa de menor toque no orquestrador.
//
// VOLTAR: na fase de treino, volta para a fase de sono. É o que o `onVoltarInterno`
// registra no orquestrador — a seta do cabeçalho da conversa desfaz UMA coisa
// por toque, e aqui há duas coisas dentro de um step.
//
// PERÍODO É DERIVADO, NÃO PERSISTIDO: Manhã/Tarde/Noite só setam a hora
// (7/13/19) e ficam ativos quando a hora cai na faixa. O que vai para o
// backend continua sendo `EXERCISETIME`, a hora cheia, no MESMO campo de
// sempre.
//
// STEPPER, NÃO `<input type="time">`: o seletor nativo do sistema é a última
// tela de design de outro fabricante que ainda aparecia no onboarding. Os
// horários de rotina são de hora cheia, então −/+ dá conta e o desenho fecha.
// A nota de janela de sono é INFORMATIVA: não bloqueia o Continuar.

import React, { useEffect, useMemo, useState } from "react";

import { CardHora, Nota, Periodos } from "../widgets/Widgets.jsx";
import s from "./StepSchedule.module.scss";

/** Faixas dos períodos e a hora que cada card seta (§ 3). */
const PERIODOS = [
  { nome: "Manhã", de: 6, ate: 11, hora: 7 },
  { nome: "Tarde", de: 12, ate: 17, hora: 13 },
  { nome: "Noite", de: 18, ate: 22, hora: 19 },
];

const hhmm = (h) => `${String(((h % 24) + 24) % 24).padStart(2, "0")}:00`;
const horaDe = (texto) => {
  const n = Number(String(texto || "").split(":")[0]);
  return Number.isFinite(n) ? n : null;
};

const StepSchedule = ({
  setCanProceed,
  setStepData,
  setNeedsButton,
  registrarVoltarInterno,
}) => {
  const [acorda, setAcorda] = useState(7);
  const [dorme, setDorme] = useState(23);
  const [treino, setTreino] = useState(null);
  const [fase, setFase] = useState("sono");

  // O step nunca avançou sozinho e continua assim: o botão é pedido
  // explicitamente para as duas fases.
  useEffect(() => {
    setNeedsButton?.(true);
    return () => setNeedsButton?.(false);
  }, [setNeedsButton]);

  // Enquanto estamos na fase de treino, a seta de voltar desfaz a FASE, não o
  // step. O orquestrador não sabe que a fase existe — é por isso que ela é
  // registrada, e não deduzida.
  useEffect(() => {
    if (!registrarVoltarInterno) return undefined;
    registrarVoltarInterno(fase === "treino" ? () => setFase("sono") : null);
    return () => registrarVoltarInterno(null);
  }, [fase, registrarVoltarInterno]);

  /* A duração do sono é conta, não pergunta. Atravessa a meia-noite pelo
     módulo de 24 — dormir 23h e acordar 7h dá 8h, não −16h. */
  const duracaoSono = useMemo(() => ((acorda - dorme) + 24) % 24 || 24, [acorda, dorme]);

  // A MESMA validação e o MESMO payload de antes: os três horários, num só
  // objeto SCHEDULE.
  useEffect(() => {
    const valido = treino != null;
    setCanProceed(valido);

    if (!valido) {
      setStepData(null);
      return;
    }

    setStepData({
      SCHEDULE: {
        WAKETIME: hhmm(acorda),
        SLEEPTIME: hhmm(dorme),
        EXERCISETIME: hhmm(treino),
        SLEEPDURATION: `${duracaoSono}h00`,
      },
    });
  }, [acorda, dorme, treino, duracaoSono, setCanProceed, setStepData]);

  const periodoAtivo = useMemo(
    () => PERIODOS.find((p) => treino != null && treino >= p.de && treino <= p.ate)?.nome,
    [treino]
  );

  const foraDaJanela = treino != null && (treino >= dorme || treino < acorda);

  if (fase === "sono") {
    return (
      <div className={s.fase}>
        <div className={s.horarios}>
          <CardHora
            rotulo="ACORDO ÀS"
            valor={hhmm(acorda)}
            onMenos={() => setAcorda((h) => (h + 23) % 24)}
            onMais={() => setAcorda((h) => (h + 1) % 24)}
          />
          <CardHora
            rotulo="DURMO ÀS"
            valor={hhmm(dorme)}
            onMenos={() => setDorme((h) => (h + 23) % 24)}
            onMais={() => setDorme((h) => (h + 1) % 24)}
          />
        </div>

        <Nota>≈ {duracaoSono}h de sono por noite</Nota>

        <button
          type="button"
          className={s.avancar}
          onClick={() => {
            // Entra com 19:00 quando ainda não há hora — o padrão do desenho.
            if (treino == null) setTreino(19);
            setFase("treino");
          }}
        >
          Continuar
        </button>
      </div>
    );
  }

  return (
    <div className={s.fase}>
      <Periodos
        lista={PERIODOS}
        ativo={periodoAtivo}
        onSelecionar={(p) => setTreino(p.hora)}
      />

      <div className={s.cardTreino}>
        <CardHora
          rotulo="TREINO ÀS"
          valor={hhmm(treino ?? 19)}
          onMenos={() => setTreino((h) => (((h ?? 19) + 23) % 24))}
          onMais={() => setTreino((h) => (((h ?? 19) + 1) % 24))}
          grande
        />
        <Nota alerta={foraDaJanela}>
          {foraDaJanela
            ? "Fora da sua janela de sono — confere se dá certo."
            : `Cabe bem entre acordar ${hhmm(acorda)} e dormir ${hhmm(dorme)}.`}
        </Nota>
      </div>
    </div>
  );
};

export default StepSchedule;
